import React, {useMemo, useState} from 'react';
import {useSelector} from "react-redux";
import {CButton, CInput, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CSelect, CTextarea} from '@coreui/react';
import {getApi} from "../../apis/backendApis";
import useAlert from "../../services/useAlert";
import useLoading from "../../services/useLoading";
import {States} from "../../store/ducks/rootReducer";
import {
    Evento,
    ItemProcesso,
    Processo,
    RegraObservacao,
    SituacaoEvento,
    TIPOS_EVENTO,
    TIPO_PAPEL_CLIENTE,
    TipoItem,
    dataBr,
    erroDaApi,
    hojeIso,
    resolvido,
    tipoSugerido,
} from "./modelo";

interface Props {
    processo: Processo,
    evento: Evento | null,
    /** Novo evento: dia ("AAAA-MM-DD") ou inicio e fim ("AAAA-MM-DD HH:MM") escolhidos no calendario. */
    dataInicial?: string,
    inicioInicial?: string,
    fimInicial?: string,
    /** Novo evento a partir do item ou da etapa (#484909). */
    itemInicial?: number | null,
    etapaInicial?: number | null,
    usuarios: Array<{id: number, nome: string}>,
    onFechar: () => void,
    aoSalvar: () => void,
    onAbrirItem?: (itemId: number) => void,
    onAbrirProcesso?: () => void,
}

interface Externo {
    nome: string,
    email: string,
}

const ehAtivo = (processo: Processo) => processo.situacao === 1 || processo.situacao === 2;

const maisUmaHora = (hora: string) => {
    const [h, m] = hora.split(':').map(Number);
    return `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** Check simples: o evento realizado ja conclui o item. */
const concluiNoEvento = (item: ItemProcesso) => item.tipo === TipoItem.CHECK && item.observacao_regra !== RegraObservacao.OBRIGATORIA && !item.exige_anexo;

/**
 * Criar ou alterar um evento da agenda do processo, opcionalmente ligado a um
 * item da checklist (#484909): o item sugere titulo, tipo, responsavel e o
 * contato do cliente, avisa se o evento passa do prazo (e oferece reprogramar)
 * e, quando o evento e realizado, oferece concluir o item. Conflito de
 * horario de algum participante volta como aviso (nao impede salvar).
 */
const EventoModal = ({processo, evento, dataInicial, inicioInicial, fimInicial, itemInicial, etapaInicial, usuarios, onFechar, aoSalvar, onAbrirItem, onAbrirProcesso}: Props) => {
    const [showAlert] = useAlert();
    const [setLoading] = useLoading();
    const eu = useSelector((state: States) => state.login.data?.user?.id) as number | undefined;

    const itens = useMemo(() => {
        const mapa = new Map<number, {item: ItemProcesso, etapaId: number}>();
        processo.etapas.forEach(etapa => etapa.itens.forEach(item => mapa.set(item.id, {item, etapaId: etapa.id})));
        return mapa;
    }, [processo]);
    const papeis = useMemo(() => new Map(processo.papeis.map(p => [p.id, p])), [processo]);
    const itemDe = (id: number | '' | null | undefined) => (id ? itens.get(Number(id))?.item : undefined);

    // Novo evento: o item sugere o titulo, o tipo, a data (o prazo, se ainda nao passou) e quem participa.
    const itemSugestao = evento ? undefined : itemDe(itemInicial);
    const hoje = hojeIso();
    const sugestaoData = itemSugestao && itemSugestao.prazo >= hoje ? itemSugestao.prazo : hoje;
    const inicio = evento?.inicio ?? inicioInicial ?? `${dataInicial ?? sugestaoData} 10:00`;
    const fim = evento ? evento.fim : (fimInicial ?? `${inicio.slice(0, 10)} ${maisUmaHora(inicio.slice(11, 16))}`);

    const [tipo, setTipo] = useState<number>(evento?.tipo ?? (itemSugestao ? tipoSugerido(itemSugestao.titulo) : 2));
    const [tipoEscolhido, setTipoEscolhido] = useState(!!evento);
    const [titulo, setTitulo] = useState(evento?.titulo ?? itemSugestao?.titulo ?? '');
    const [data, setData] = useState(inicio.slice(0, 10));
    const [horaInicio, setHoraInicio] = useState(inicio.slice(11, 16));
    const [horaFim, setHoraFim] = useState(fim ? fim.slice(11, 16) : '');
    const [local, setLocal] = useState(evento?.local ?? '');
    const [itemId, setItemId] = useState<number | ''>(evento?.item_id ?? itemInicial ?? '');
    const [etapaId, setEtapaId] = useState<number | ''>(evento?.etapa_id ?? (itemInicial ? itens.get(itemInicial)?.etapaId : undefined) ?? etapaInicial ?? '');
    const [visivel, setVisivel] = useState(evento?.visivel_cliente ?? true);
    const [internos, setInternos] = useState<number[]>(() => {
        if (evento) return evento.participantes.filter(p => p.usuario_id).map(p => Number(p.usuario_id));
        const quem = [eu, itemSugestao?.quem_age].filter((id): id is number => !!id);
        return Array.from(new Set(quem));
    });
    const [externos, setExternos] = useState<Externo[]>(() => {
        if (evento) return evento.participantes.filter(p => !p.usuario_id).map(p => ({nome: p.nome, email: p.email || ''}));
        const papel = itemSugestao ? papeis.get(itemSugestao.papel_id) : undefined;
        return papel?.tipo === TIPO_PAPEL_CLIENTE && papel.contato?.nome ? [{nome: papel.contato.nome, email: papel.contato.email || ''}] : [];
    });
    const [motivo, setMotivo] = useState('');
    const [cancelando, setCancelando] = useState(false);
    const [avisos, setAvisos] = useState<string[]>([]);
    const [reprogramarItem, setReprogramarItem] = useState(false);
    const [realizado, setRealizado] = useState(false);

    // Quem ja esta no processo aparece primeiro.
    const doProcesso = useMemo(() => {
        const mapa = new Map<number, string>();
        processo.papeis.forEach(p => p.usuario && mapa.set(p.usuario.id, p.usuario.nome));
        return Array.from(mapa.entries()).map(([id, nome]) => ({id, nome}));
    }, [processo]);
    // Contatos do cliente (#484909): os cadastrados (inclusive de outros agendamentos e processos) e os dos papeis.
    const chaveContato = (nome: string, email?: string | null) => (email ? `e:${email.trim().toLowerCase()}` : `n:${nome.trim().toLowerCase()}`);
    const contatosCliente = useMemo(() => {
        const mapa = new Map<string, {nome: string, email: string | null, cargo?: string | null}>();
        (processo.contatos_cliente || []).forEach(c => mapa.set(chaveContato(c.nome, c.email), c));
        processo.papeis.filter(p => p.contato?.nome).forEach(p => {
            const chave = chaveContato(p.contato!.nome!, p.contato!.email);
            if (!mapa.has(chave)) mapa.set(chave, {nome: p.contato!.nome!, email: p.contato!.email ?? null, cargo: p.contato!.cargo});
        });
        return Array.from(mapa.values()).sort((a, b) => a.nome.localeCompare(b.nome));
    }, [processo]);
    const contatosDisponiveis = contatosCliente.filter(c => !externos.some(e => chaveContato(e.nome, e.email) === chaveContato(c.nome, c.email)));
    const editavel = ehAtivo(processo) && (!evento || evento.situacao === SituacaoEvento.AGENDADO);

    const item = itemDe(itemId);
    const itemPendente = item && !resolvido(item) ? item : undefined;
    const etapaDoItem = item ? processo.etapas.find(e => e.id === itens.get(item.id)?.etapaId) : undefined;
    // Gestor ou responsavel pela etapa reprogramam o prazo (a API confere de novo).
    const podeReprogramarItem = !!etapaDoItem && ehAtivo(processo)
        && (processo.pode_gerir || (!!eu && papeis.get(etapaDoItem.papel_id)?.usuario?.id === eu));
    const depoisDoPrazo = !!itemPendente && !!data && data > itemPendente.prazo;

    // Itens que podem ser escolhidos: os pendentes da etapa (ou de todas) e o que ja esta ligado.
    const opcoesItens = processo.etapas
        .filter(etapa => !etapaId || etapa.id === etapaId)
        .map(etapa => ({etapa, itens: etapa.itens.filter(i => !resolvido(i) || i.id === itemId)}))
        .filter(grupo => grupo.itens.length > 0);

    const escolheItem = (valor: string) => {
        const novo = itemDe(valor ? Number(valor) : '');
        setItemId(novo ? novo.id : '');
        setReprogramarItem(false);
        if (!novo) return;
        setEtapaId(itens.get(novo.id)!.etapaId);
        if (!titulo.trim() || titulo === item?.titulo) setTitulo(novo.titulo);
        if (!tipoEscolhido) setTipo(tipoSugerido(novo.titulo));
    };

    const escolheEtapa = (valor: string) => {
        const nova = valor ? Number(valor) : '';
        setEtapaId(nova);
        if (item && nova && itens.get(item.id)?.etapaId !== nova) {
            setItemId('');
            setReprogramarItem(false);
        }
    };

    const alternaInterno = (id: number) => setInternos(internos.includes(id) ? internos.filter(i => i !== id) : [...internos, id]);

    const enviar = (rota: string, dados: any, mensagemErro = 'Erro ao salvar o evento.') => {
        const corpo = new FormData();
        corpo.append('data', JSON.stringify(dados));
        setLoading(true);
        return getApi().post(rota, corpo)
            .then(resposta => {
                showAlert({msg: resposta.data.msg, type: 'success', toaster: true});
                return resposta.data;
            })
            .catch(erro => {
                showAlert({title: 'Não foi possível', msg: erroDaApi(erro, mensagemErro), type: 'danger', toaster: true});
                return null;
            })
            .finally(() => setLoading(false));
    };

    const salvar = async () => {
        const participantes = [
            ...internos.map(id => ({usuario_id: id})),
            ...externos.filter(e => e.nome.trim()).map(e => ({nome: e.nome.trim(), email: e.email.trim()})),
        ];
        const dados = {
            tipo, titulo, local, participantes,
            inicio: `${data} ${horaInicio || '00:00'}`,
            fim: horaFim ? `${data} ${horaFim}` : null,
            etapa_id: etapaId || null,
            item_id: itemId || null,
            visivel_cliente: visivel,
        };
        const resposta = await enviar(evento ? `processo/evento/editar/${evento.id}` : `processo/evento/novo/${processo.id}`, dados);
        if (!resposta) return;
        let lista: string[] = resposta.avisos || [];
        if (reprogramarItem && depoisDoPrazo && itemPendente) {
            const nomeEvento = titulo.trim() || TIPOS_EVENTO.getDescription(tipo);
            const reprogramou = await enviar(`processo/item/reprogramar/${itemPendente.id}`,
                {prazo: data, motivo: `Evento “${nomeEvento}” agendado para ${dataBr(data)}.`}, 'Erro ao reprogramar o prazo do item.');
            if (reprogramou) {
                lista = lista.filter(aviso => !aviso.includes('depois do prazo do item'));
            }
        }
        aoSalvar();
        if (lista.length) {
            setAvisos(lista);
        } else {
            onFechar();
        }
    };

    const mudaSituacao = (situacao: SituacaoEvento) => {
        enviar(`processo/evento/situacao/${evento!.id}`, {situacao, motivo}).then(resposta => {
            if (!resposta) return;
            aoSalvar();
            // Realizado com item pendente: o proximo passo e o item.
            if (situacao === SituacaoEvento.REALIZADO && itemPendente) {
                setRealizado(true);
            } else {
                onFechar();
            }
        });
    };

    const concluirItem = () => {
        if (!itemPendente) return;
        enviar(`processo/item/responder/${itemPendente.id}`,
            {resposta: true, observacao: `Realizado em ${dataBr(data)} (${evento?.titulo ?? titulo}).`, respondido_pelo_cliente: false},
            'Erro ao concluir o item.').then(resposta => {
            if (resposta) {
                aoSalvar();
                onFechar();
            }
        });
    };

    const anexarAta = (arquivos: FileList | null) => {
        if (!arquivos || !arquivos.length || !evento) return;
        const corpo = new FormData();
        corpo.append('data', JSON.stringify({evento_id: evento.id, etapa_id: evento.etapa_id, item_id: evento.item_id, categoria: 5, visivel_cliente: visivel}));
        Array.from(arquivos).forEach((arquivo, i) => corpo.append(`arquivo${i}`, arquivo));
        setLoading(true);
        getApi().post(`processo/anexo/${processo.id}`, corpo)
            .then(resposta => {
                showAlert({msg: resposta.data.msg, type: 'success', toaster: true});
                aoSalvar();
            })
            .catch(erro => showAlert({title: 'Não foi possível', msg: erroDaApi(erro, 'Erro ao anexar.'), type: 'danger', toaster: true}))
            .finally(() => setLoading(false));
    };

    const tituloModal = evento
        ? `${evento.tipo_nome} · ${evento.situacao_nome}`
        : (itemSugestao ? `Agendar “${itemSugestao.titulo}”` : 'Novo evento');

    if (realizado && itemPendente) {
        return (
            <CModal show onClose={onFechar} centered>
                <CModalHeader closeButton>
                    <CModalTitle>Evento realizado</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <p className="pr-evento-realizado">O item <strong>“{itemPendente.titulo}”</strong> continua pendente.</p>
                    {!(concluiNoEvento(itemPendente) && itemPendente.pode_executar) && (
                        <p className="pr-ajuda">Ele pede mais que um “feito” (resposta, observação ou anexo): abra o item para concluir.</p>
                    )}
                </CModalBody>
                <CModalFooter>
                    <CButton color="dark" variant="outline" onClick={onFechar}>Agora não</CButton>
                    {onAbrirItem && <CButton color="primary" variant="outline" onClick={() => { onFechar(); onAbrirItem(itemPendente.id); }}>Abrir o item</CButton>}
                    {concluiNoEvento(itemPendente) && itemPendente.pode_executar && <CButton color="success" onClick={concluirItem}>Concluir o item</CButton>}
                </CModalFooter>
            </CModal>
        );
    }

    return (
        <CModal show onClose={onFechar} size="lg" centered>
            <CModalHeader closeButton>
                <CModalTitle>{tituloModal}</CModalTitle>
            </CModalHeader>
            <CModalBody>
                {avisos.length > 0 && (
                    <div className="pr-aviso">
                        <strong>{avisos.some(a => !a.includes('depois do prazo do item')) ? 'Salvo, mas atenção ao conflito de horário:' : 'Salvo, mas atenção:'}</strong>
                        <ul>{avisos.map((a, i) => <li key={i}>{a}</li>)}</ul>
                    </div>
                )}
                <div className="pr-evento-grade">
                    <label>
                        <span>Tipo</span>
                        <CSelect value={tipo} disabled={!editavel} onChange={(e: any) => { setTipo(Number(e.target.value)); setTipoEscolhido(true); }}>
                            {TIPOS_EVENTO.getList().map((t: any) => <option key={t.value} value={t.value}>{t.description}</option>)}
                        </CSelect>
                    </label>
                    <label className="largo">
                        <span>Título</span>
                        <CInput value={titulo} maxLength={150} disabled={!editavel} placeholder={TIPOS_EVENTO.getDescription(tipo)} onChange={(e: any) => setTitulo(e.target.value)}/>
                    </label>
                    <label>
                        <span>Data</span>
                        <CInput type="date" value={data} disabled={!editavel} onChange={(e: any) => setData(e.target.value)}/>
                    </label>
                    <label>
                        <span>Início</span>
                        <CInput type="time" value={horaInicio} disabled={!editavel} onChange={(e: any) => setHoraInicio(e.target.value)}/>
                    </label>
                    <label>
                        <span>Fim</span>
                        <CInput type="time" value={horaFim} disabled={!editavel} onChange={(e: any) => setHoraFim(e.target.value)}/>
                    </label>
                    <label className="largo">
                        <span>Local ou link</span>
                        <CInput value={local} maxLength={300} disabled={!editavel} placeholder="Sala, endereço ou link da reunião" onChange={(e: any) => setLocal(e.target.value)}/>
                    </label>
                    <label>
                        <span>Etapa</span>
                        <CSelect value={etapaId} disabled={!editavel} onChange={(e: any) => escolheEtapa(e.target.value)}>
                            <option value="">—</option>
                            {processo.etapas.map(e => <option key={e.id} value={e.id}>{e.ordem}. {e.nome}</option>)}
                        </CSelect>
                    </label>
                    <label className="inteiro">
                        <span>
                            Item da checklist (opcional)
                            {item && onAbrirItem && evento && <button type="button" className="pr-link pr-evento-abrir-item" onClick={() => { onFechar(); onAbrirItem(item.id); }}>abrir o item</button>}
                        </span>
                        <CSelect value={itemId} disabled={!editavel} onChange={(e: any) => escolheItem(e.target.value)} aria-label="Item da checklist">
                            <option value="">— nenhum —</option>
                            {opcoesItens.map(({etapa, itens: lista}) => (
                                <optgroup key={etapa.id} label={`${etapa.ordem}. ${etapa.nome}`}>
                                    {lista.map(i => <option key={i.id} value={i.id}>{i.titulo}{resolvido(i) ? ' (resolvido)' : ` · prazo ${dataBr(i.prazo).slice(0, 5)}`}</option>)}
                                </optgroup>
                            ))}
                        </CSelect>
                    </label>
                </div>

                {editavel && depoisDoPrazo && itemPendente && (
                    <div className="pr-aviso pr-evento-prazo">
                        O evento fica depois do prazo do item ({dataBr(itemPendente.prazo)}).
                        {podeReprogramarItem ? (
                            <label className="pr-opcao pequena">
                                <input type="checkbox" checked={reprogramarItem} onChange={e => setReprogramarItem(e.target.checked)}/> Reprogramar o prazo do item para {dataBr(data)}
                            </label>
                        ) : <span> Só o gestor ou o responsável pela etapa reprogramam o prazo.</span>}
                    </div>
                )}

                <div className="pr-evento-participantes">
                    <div className="pr-bloco-titulo">Participantes da equipe</div>
                    <div className="pr-evento-pessoas">
                        {doProcesso.map(u => (
                            <label key={u.id} className={`pr-pessoa ${internos.includes(u.id) ? 'marcada' : ''}`}>
                                <input type="checkbox" checked={internos.includes(u.id)} disabled={!editavel} onChange={() => alternaInterno(u.id)}/> {u.nome}
                            </label>
                        ))}
                        {internos.filter(id => !doProcesso.some(u => u.id === id)).map(id => (
                            <label key={id} className="pr-pessoa marcada">
                                <input type="checkbox" checked disabled={!editavel} onChange={() => alternaInterno(id)}/> {usuarios.find(u => u.id === id)?.nome ?? `Usuário ${id}`}
                            </label>
                        ))}
                        {editavel && (
                            <CSelect value="" onChange={(e: any) => e.target.value && alternaInterno(Number(e.target.value))} className="pr-evento-mais">
                                <option value="">+ outra pessoa</option>
                                {usuarios.filter(u => !internos.includes(u.id)).map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                            </CSelect>
                        )}
                    </div>
                    <div className="pr-bloco-titulo">Participantes do cliente</div>
                    {externos.map((externo, i) => (
                        <div key={i} className="pr-evento-externo">
                            <CInput placeholder="Nome" value={externo.nome} disabled={!editavel}
                                    onChange={(e: any) => setExternos(externos.map((x, j) => (j === i ? {...x, nome: e.target.value} : x)))}/>
                            <CInput placeholder="E-mail" value={externo.email} disabled={!editavel}
                                    onChange={(e: any) => setExternos(externos.map((x, j) => (j === i ? {...x, email: e.target.value} : x)))}/>
                            {editavel && <button type="button" className="pr-link perigo" onClick={() => setExternos(externos.filter((_, j) => j !== i))}>remover</button>}
                        </div>
                    ))}
                    {editavel && (
                        <div className="pr-evento-atalhos">
                            {contatosDisponiveis.length > 0 && (
                                <CSelect value="" className="pr-evento-contatos" aria-label="Adicionar contato do cliente" onChange={(e: any) => {
                                    const contato = contatosDisponiveis[Number(e.target.value)];
                                    if (contato) setExternos([...externos, {nome: contato.nome, email: contato.email || ''}]);
                                }}>
                                    <option value="">+ contato do cliente já cadastrado…</option>
                                    {contatosDisponiveis.map((c, i) => <option key={i} value={i}>{c.nome}{c.email ? ` · ${c.email}` : ''}{c.cargo ? ` (${c.cargo})` : ''}</option>)}
                                </CSelect>
                            )}
                            <button type="button" className="pr-link" onClick={() => setExternos([...externos, {nome: '', email: ''}])}>+ novo participante do cliente</button>
                            <small className="pr-ajuda">Quem é novo entra nos contatos do cliente ao salvar.</small>
                        </div>
                    )}
                    <label className="pr-opcao pequena">
                        <input type="checkbox" checked={visivel} disabled={!editavel} onChange={e => setVisivel(e.target.checked)}/> visível ao cliente
                    </label>
                </div>

                {evento && evento.situacao === SituacaoEvento.AGENDADO && ehAtivo(processo) && (
                    <div className="pr-evento-situacao">
                        {!cancelando ? (
                            <>
                                <CButton color="success" variant="outline" onClick={() => mudaSituacao(SituacaoEvento.REALIZADO)}>Marcar como realizado</CButton>
                                <CButton color="danger" variant="outline" onClick={() => setCancelando(true)}>Cancelar evento</CButton>
                            </>
                        ) : (
                            <>
                                <CTextarea rows={2} placeholder="Motivo do cancelamento" value={motivo} onChange={(e: any) => setMotivo(e.target.value)}/>
                                <CButton color="danger" disabled={motivo.trim().length < 5} onClick={() => mudaSituacao(SituacaoEvento.CANCELADO)}>Confirmar cancelamento</CButton>
                            </>
                        )}
                    </div>
                )}
                {evento && ehAtivo(processo) && (
                    <label className="pr-enviar pr-evento-ata">
                        Anexar ata ou gravação{evento.item_id ? ' (vai para o item)' : ''}
                        <input type="file" multiple onChange={e => anexarAta(e.target.files)}/>
                    </label>
                )}
                {evento?.criado_por && <p className="pr-ajuda mt-2">Agendado por {evento.criado_por}.</p>}
            </CModalBody>
            <CModalFooter>
                {onAbrirProcesso && <button type="button" className="pr-link mr-auto" onClick={() => { onFechar(); onAbrirProcesso(); }}>Abrir o processo</button>}
                <CButton color="dark" variant="outline" onClick={onFechar}>{avisos.length ? 'Fechar' : 'Cancelar'}</CButton>
                {editavel && !avisos.length && <CButton color="primary" onClick={salvar}>{evento ? 'Salvar' : 'Agendar'}</CButton>}
            </CModalFooter>
        </CModal>
    );
};

export default EventoModal;
